<template>
    <!-- Mobile Overlay -->
    <div
        v-if="isOpen && isMobile"
        @click="$emit('close')"
        class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden transition-opacity"
        :class="isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'"
    ></div>

    <!-- Sidebar -->
    <aside
        :class="[
            'fixed md:static inset-y-0 left-0 z-50 w-64 bg-noir-800/95 backdrop-blur-md border-r border-gold-500/20 flex flex-col transition-transform duration-300',
            {
                'md:flex': true,
                'translate-x-0': isOpen || !isMobile,
                '-translate-x-full': !isOpen && isMobile,
            },
        ]"
    >
        <!-- Logo/Brand -->
        <div
            class="p-6 border-b border-gold-500/20 flex items-center justify-between"
        >
            <h1 class="text-xl font-bold font-display text-gold-400">
                TFS Admin
            </h1>
            <button
                v-if="isMobile"
                @click="$emit('close')"
                class="md:hidden text-slate-400 hover:text-white"
                aria-label="Close sidebar"
            >
                <svg
                    class="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                    />
                </svg>
            </button>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 p-4 space-y-1 overflow-y-auto">
            <template v-for="item in visibleNavItems" :key="item.name">
                <!-- Group with children -->
                <div v-if="item.children && item.children.length > 0">
                    <button
                        @click="handleParentClick(item)"
                        class="w-full flex items-center justify-between px-4 py-2 rounded-lg text-cream-200/70 hover:text-gold-400 hover:bg-gold-500/10 transition-all duration-200"
                        :class="{
                            'bg-gold-500/15 text-gold-400 font-medium border-l-2 border-gold-400': isParentActive(item),
                        }"
                        :aria-expanded="expandedItems.has(item.path ?? item.name)"
                        :aria-controls="`nav-group-${(item.path ?? item.name).replace(/\s+/g, '-').toLowerCase()}`"
                    >
                        <span>{{ item.name }}</span>
                        <svg
                            class="w-4 h-4 transition-transform duration-200"
                            :class="{ 'rotate-90': expandedItems.has(item.path ?? item.name) }"
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            aria-hidden="true"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                    <!-- Children -->
                    <div
                        v-show="expandedItems.has(item.path ?? item.name)"
                        :id="`nav-group-${(item.path ?? item.name).replace(/\s+/g, '-').toLowerCase()}`"
                        class="ml-4 space-y-1 border-l border-gold-500/10"
                    >
                        <RouterLink
                            v-for="child in visibleChildren(item)"
                            :key="child.path"
                            :to="child.path"
                            @click="handleNavClick"
                            class="block px-4 py-2 rounded-lg text-cream-200/70 hover:text-gold-400 hover:bg-gold-500/10 transition-all duration-200 text-sm"
                            active-class="bg-gold-500/15 text-gold-400 font-medium border-l-2 border-gold-400"
                        >
                            {{ child.name }}
                        </RouterLink>
                    </div>
                </div>
                <!-- Leaf item (no children) -->
                <RouterLink
                    v-else-if="item.path"
                    :to="item.path"
                    @click="handleNavClick"
                    class="block px-4 py-2 rounded-lg text-cream-200/70 hover:text-gold-400 hover:bg-gold-500/10 transition-all duration-200"
                    active-class="bg-gold-500/15 text-gold-400 font-medium border-l-2 border-gold-400"
                >
                    {{ item.name }}
                </RouterLink>
            </template>
        </nav>

        <!-- User Info & Logout -->
        <div class="p-4 border-t border-gold-500/20">
            <div class="flex items-center gap-3 mb-4">
                <div
                    class="w-8 h-8 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center text-sm font-bold text-noir-900"
                >
                    {{ userInitial }}
                </div>
                <div class="text-sm truncate flex-1">
                    <p class="font-medium text-cream-100">
                        {{ user?.name || "User" }}
                    </p>
                    <p class="text-cream-200/50 text-xs truncate">
                        {{ user?.email || "" }}
                    </p>
                </div>
            </div>
            <button
                @click="$emit('logout')"
                class="w-full px-4 py-2 text-sm text-burgundy-400 hover:bg-burgundy-500/10 rounded-lg text-left transition-all duration-200 flex items-center gap-2 border border-transparent hover:border-burgundy-500/30"
            >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                </svg>
                Sign out
            </button>
        </div>
    </aside>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import { RouterLink, useRouter, useRoute } from "vue-router";
import type { UserEntity } from "@/modules/auth/domain/entities/User";
import { usePermissions } from "@/composables/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";
import type { PermissionSlug } from "@/constants/permissions";

/**
 * Navigation item with optional nested children for 2-level sidebar menus.
 * @example { name: "Galleries", path: "/admin/galleries", children: [{ name: "Trash", path: "/admin/galleries/trash" }] }
 */
type NavItem = {
    name: string;
    path?: string;
    permission?: PermissionSlug;
    exactAnyPermissions?: PermissionSlug[];
    children?: NavItem[];
};

type NavItemWithPath = NavItem & {
    path: string;
};

type Props = {
    isOpen: boolean;
    user?: UserEntity | null;
};

type Emits = {
    (e: "close"): void;
    (e: "logout"): void;
};

const props = withDefaults(defineProps<Props>(), {
    isOpen: false,
    user: null,
});

const emit = defineEmits<Emits>();
const { can, hasAnyExact } = usePermissions();

const navItems: NavItem[] = [
    { name: "Dashboard", path: "/admin/dashboard", permission: PERMISSIONS.ADMIN.DASHBOARD },
    {
        name: "Operations",
        children: [
            { name: "Activity Logs", path: "/admin/activity-logs", permission: PERMISSIONS.ADMIN.ACTIVITY_LOGS.VIEW },
            { name: "Procesamiento de Imágenes", path: "/admin/media/processing", permission: PERMISSIONS.ADMIN.MEDIA.MONITOR },
        ],
    },
    {
        name: "Galleries",
        path: "/admin/galleries",
        permission: PERMISSIONS.ADMIN.GALLERIES.VIEW,
        children: [
            {
                name: "Trash",
                path: "/admin/galleries/trash",
                exactAnyPermissions: [PERMISSIONS.GALLERIES.DELETE_OWN, PERMISSIONS.GALLERIES.DELETE_ALL],
            },
        ],
    },
    { name: "Videos", path: "/admin/videos", exactAnyPermissions: [PERMISSIONS.VIDEOS.UPLOAD_OWN, PERMISSIONS.VIDEOS.UPLOAD_ALL] },
    { name: "Categories", path: "/admin/categories", permission: PERMISSIONS.ADMIN.CATEGORIES.VIEW },
    { name: "Redirects", path: "/admin/redirects", permission: PERMISSIONS.ADMIN.REDIRECTS.VIEW },
    { name: "Orders", path: "/admin/orders", permission: PERMISSIONS.ADMIN.ORDERS.VIEW },
    { name: "Promotions", path: "/admin/promotions", permission: PERMISSIONS.PROMOTIONS.VIEW_ALL },
    { name: "Profiles", path: "/admin/profiles", permission: PERMISSIONS.ADMIN.PROFILES.VIEW },
    { name: "Users", path: "/admin/users", permission: PERMISSIONS.ADMIN.USERS.VIEW },
    {
        name: 'Roles & Permissions',
        children: [
            { name: 'Roles', path: '/admin/roles', permission: PERMISSIONS.ADMIN.ROLES.VIEW },
            { name: 'Permissions', path: '/admin/permissions', permission: PERMISSIONS.ADMIN.PERMISSIONS.VIEW },
        ],
    },
    { name: "Reviews", path: "/admin/reviews", permission: PERMISSIONS.ADMIN.REVIEWS.MODERATE },
    { name: "Settings", path: "/admin/settings", permission: PERMISSIONS.ADMIN.SETTINGS.VIEW },
];

const router = useRouter();
const route = useRoute();

const EXPANDED_STORAGE_KEY = "admin-sidebar-expanded";

const loadExpandedFromStorage = (): string[] => {
    try {
        const stored = localStorage.getItem(EXPANDED_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
};

const saveExpandedToStorage = (items: Set<string>) => {
    localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify([...items]));
};

const expandedItems = ref<Set<string>>(new Set(loadExpandedFromStorage()));

/**
 * Toggles expanded state for a parent item, persists to localStorage.
 * Uses the item's path as the stable key (falls back to name).
 * @param item - The parent nav item to toggle
 */
const toggleExpand = (item: NavItem) => {
    const key = item.path ?? item.name;
    const newSet = new Set(expandedItems.value);
    if (newSet.has(key)) {
        newSet.delete(key);
    } else {
        newSet.add(key);
    }
    expandedItems.value = newSet;
    saveExpandedToStorage(newSet);
};

/**
 * Checks if a parent nav item is active (current route matches parent or any child).
 * @param item - The parent nav item to check
 * @returns True if the current route matches the parent path or any child's path
 */
const isParentActive = (item: NavItem): boolean => {
    if (route.path === item.path) return true;
    if (item.children) {
        return item.children.some(child => route.path === child.path);
    }
    return false;
};

/**
 * Returns visible children filtered by permission (supports both single permission and exactAnyPermissions).
 * @param item - The parent nav item whose children to filter
 * @returns Array of visible child items
 */
const visibleChildren = (item: NavItem): NavItemWithPath[] => {
    if (!item.children) return [];
    return item.children.filter((child): child is NavItemWithPath => {
        if (!child.path) return false;
        if (child.permission) return can(child.permission);
        return child.exactAnyPermissions ? hasAnyExact(child.exactAnyPermissions) : false;
    });
};

/**
 * Toggles expand/collapse for a parent nav item and navigates to its route.
 * Intentionally does NOT close the sidebar on mobile — the user needs to
 * see the expanded children before choosing a destination.
 * @param item - The parent nav item that was clicked
 */
const handleParentClick = (item: NavItem) => {
    toggleExpand(item);
    if (item.path) {
        router.push(item.path);
    }
};

/**
 * Computed list of nav items filtered by permissions (parents shown if they pass or have visible children).
 */
const visibleNavItems = computed(() => {
    return navItems.filter(item => {
        if (item.children) {
            const parentPasses = item.permission
                ? can(item.permission)
                : (item.exactAnyPermissions ? hasAnyExact(item.exactAnyPermissions) : false);
            const hasVisibleChildren = item.children.some(child => {
                if (child.permission) return can(child.permission);
                return child.exactAnyPermissions ? hasAnyExact(child.exactAnyPermissions) : false;
            });
            return parentPasses || hasVisibleChildren;
        }

        if (item.permission) return can(item.permission);
        return item.exactAnyPermissions ? hasAnyExact(item.exactAnyPermissions) : false;
    });
});

/**
 * Auto-expands parent menu when current route matches a child's path.
 * Ensures nested navigation is visible without manual expansion.
 */
watch(
    () => route.path,
    (currentPath) => {
        navItems.forEach(item => {
            if (item.children?.some(child => child.path === currentPath)) {
                const key = item.path ?? item.name;
                if (!expandedItems.value.has(key)) {
                    const newSet = new Set(expandedItems.value);
                    newSet.add(key);
                    expandedItems.value = newSet;
                    saveExpandedToStorage(newSet);
                }
            }
        });
    },
    { immediate: true }
);

const isMobile = ref(false);

const userInitial = computed(() => {
    return props.user?.name?.charAt(0)?.toUpperCase() || "U";
});

const handleNavClick = () => {
    if (isMobile.value) {
        emit("close");
    }
};

const checkMobile = () => {
    isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
    checkMobile();
    window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
    window.removeEventListener("resize", checkMobile);
});
</script>
